import React from 'react'
import { toast } from 'react-toastify'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { AxiosResponse } from 'axios'

import { axiosInstance } from '../../helpers'
import {
    Prospect,
    DetailedCampaignProspect,
    Lead,
} from '../../models/Outreach/types'
import { GuestCardFormRequest } from './useGuestCardForm'

const invalidatePipelineCounts = (
    queryClient: ReturnType<typeof useQueryClient>,
) => {
    queryClient.invalidateQueries({
        queryKey: ['leasing-season-pipeline-counts'],
        exact: false,
    })
}

export interface LeadsFilters {
    search?: string
    is_lead?: boolean
    campaign_id?: number
    leasing_season_id?: number
    pipeline_stage?: string
    pipeline_tab?: string
    lead_source?: string
    application_status?: string
    screening_status?: string
    ordering?: string
    in_campaign?: boolean
    has_email?: boolean
    has_phone?: boolean
    page?: number
    page_size?: number
}

export interface PaginatedCampaignProspectsResponse {
    count: number
    next: string | null
    previous: string | null
    results: DetailedCampaignProspect[]
}

export interface PaginatedProspectsResponse {
    count: number
    next: string | null
    previous: string | null
    results: Prospect[]
}

export interface PaginatedLeadsResponse {
    count: number
    next: string | null
    previous: string | null
    results: Lead[]
    summary_stats: {
        total_count: number
        from_email: number
        from_phone: number
        from_property: number
    }
}

// Union type for combined prospects endpoint
export type ProspectOrCampaignProspect = Prospect | DetailedCampaignProspect

export interface PaginatedProspectsAndCampaignProspectsResponse {
    count: number
    next: string | null
    previous: string | null
    results: ProspectOrCampaignProspect[]
    summary_stats: {
        total_count: number
        in_active_campaign: number
        not_in_campaign: number
        with_email: number
        with_phone: number
        with_profile: number
    }
}

// Type guard to check if item is DetailedCampaignProspect
export const isDetailedCampaignProspect = (
    item: ProspectOrCampaignProspect,
): item is DetailedCampaignProspect => {
    return 'campaign' in item && 'prospect' in item && 'current_step' in item
}

export interface UseLeadsOptions {
    apartmentId: number | undefined
    filters?: LeadsFilters
    enabled?: boolean
}

export const useLeads = (options: UseLeadsOptions) => {
    const { apartmentId, filters = {}, enabled = true } = options
    const queryClient = useQueryClient()

    // Cache summary stats separately - they shouldn't change with pagination
    const cachedSummaryStatsRef = React.useRef<{
        stats:
            | PaginatedProspectsAndCampaignProspectsResponse['summary_stats']
            | null
        filterKey: string
    }>({ stats: null, filterKey: '' })

    // Cache leads summary stats separately - they shouldn't change with pagination
    const cachedLeadsSummaryStatsRef = React.useRef<{
        stats: PaginatedLeadsResponse['summary_stats'] | null
        filterKey: string
    }>({ stats: null, filterKey: '' })

    // Create a filter key for stats (excludes page/page_size since stats don't change with pagination)
    const statsFilterKey = React.useMemo(() => {
        return JSON.stringify({
            search: filters.search || '',
            campaign_id: filters.campaign_id || null,
            in_campaign: filters.in_campaign ?? null,
            has_email: filters.has_email ?? null,
            has_phone: filters.has_phone ?? null,
        })
    }, [
        filters.search,
        filters.campaign_id,
        filters.in_campaign,
        filters.has_email,
        filters.has_phone,
    ])

    // Create a filter key for leads stats (excludes page/page_size, only includes search)
    const leadsStatsFilterKey = React.useMemo(() => {
        return JSON.stringify({
            search: filters.search || '',
        })
    }, [filters.search])

    // Serialize filters separately for each query to keep them independent
    // Prospects query filters (excludes is_lead since it's not used by the endpoint)
    const serializedProspectsFilters = React.useMemo(() => {
        return JSON.stringify({
            search: filters.search || '',
            campaign_id: filters.campaign_id || null,
            in_campaign: filters.in_campaign ?? null,
            has_email: filters.has_email ?? null,
            has_phone: filters.has_phone ?? null,
            page: filters.page || 1,
            page_size: filters.page_size || 25,
        })
    }, [
        filters.search,
        filters.campaign_id,
        filters.in_campaign,
        filters.has_email,
        filters.has_phone,
        filters.page,
        filters.page_size,
    ])

    // Leads query filters (only includes filters used by the leads endpoint)
    const serializedLeadsFilters = React.useMemo(() => {
        return JSON.stringify({
            search: filters.search || '',
            leasing_season_id: filters.leasing_season_id ?? null,
            pipeline_stage: filters.pipeline_stage ?? null,
            pipeline_tab: filters.pipeline_tab ?? null,
            lead_source: filters.lead_source ?? null,
            application_status: filters.application_status ?? null,
            screening_status: filters.screening_status ?? null,
            ordering: filters.ordering ?? null,
            page: filters.page || 1,
            page_size: filters.page_size || 25,
        })
    }, [
        filters.search,
        filters.leasing_season_id,
        filters.pipeline_stage,
        filters.pipeline_tab,
        filters.lead_source,
        filters.application_status,
        filters.screening_status,
        filters.ordering,
        filters.page,
        filters.page_size,
    ])

    // Single query: Fetch combined Prospects and Campaign Prospects
    const {
        data: combinedProspectsData,
        isLoading: isLoadingCombinedProspects,
        error: errorCombinedProspects,
        refetch: refetchCombinedProspects,
    } = useQuery<
        AxiosResponse<
            | PaginatedProspectsAndCampaignProspectsResponse
            | ProspectOrCampaignProspect[]
        >,
        Error
    >({
        queryKey: [
            'prospects-and-campaign-prospects',
            apartmentId,
            serializedProspectsFilters,
        ],
        queryFn: () => {
            const params: Record<string, string | number | boolean> = {
                apartment_id: apartmentId!,
                page: filters.page || 1,
                page_size: filters.page_size || 25,
            }

            if (filters.search) {
                params.search = filters.search
            }
            if (filters.campaign_id) {
                params.campaign_id = filters.campaign_id
            }
            if (filters.in_campaign !== undefined) {
                params.in_campaign = filters.in_campaign
            }
            if (filters.has_email !== undefined) {
                params.has_email = filters.has_email
            }
            if (filters.has_phone !== undefined) {
                params.has_phone = filters.has_phone
            }

            return axiosInstance.get(
                '/outreach/prospects/prospects-and-campaign-prospects/',
                { params },
            )
        },
        enabled: enabled && !!apartmentId && filters.is_lead !== true,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    // Query 3: Fetch Leads
    const {
        data: leadsData,
        isLoading: isLoadingLeads,
        error: errorLeads,
        refetch: refetchLeads,
    } = useQuery<AxiosResponse<PaginatedLeadsResponse | Lead[]>, Error>({
        queryKey: ['leads', apartmentId, serializedLeadsFilters],
        queryFn: () => {
            const params: Record<string, string | number | boolean> = {
                apartment_id: apartmentId!,
                page: filters.page || 1,
                page_size: filters.page_size || 25,
            }

            if (filters.search) {
                params.search = filters.search
            }

            if (filters.leasing_season_id != null) {
                params.leasing_season_id = filters.leasing_season_id
            }

            if (filters.pipeline_tab) {
                params.pipeline_tab = filters.pipeline_tab
            } else if (filters.pipeline_stage) {
                params.pipeline_stage = filters.pipeline_stage
            }

            if (filters.lead_source) {
                params.lead_source = filters.lead_source
            }

            if (filters.application_status) {
                params.application_status = filters.application_status
            }

            if (filters.screening_status) {
                params.screening_status = filters.screening_status
            }

            if (filters.ordering) {
                params.ordering = filters.ordering
            }

            return axiosInstance.get('/outreach/leads/get-leads/', { params })
        },
        enabled: enabled && !!apartmentId && filters.is_lead === true,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    // Extract and normalize combined Prospects and Campaign Prospects data
    const combinedProspectsResponseData = combinedProspectsData?.data
    let combinedProspects: ProspectOrCampaignProspect[] = []
    let combinedProspectsPagination: PaginatedProspectsAndCampaignProspectsResponse | null = null
    const isCombinedProspectsPaginated =
        combinedProspectsResponseData &&
        typeof combinedProspectsResponseData === 'object' &&
        !Array.isArray(combinedProspectsResponseData) &&
        'results' in combinedProspectsResponseData

    if (combinedProspectsResponseData) {
        if (isCombinedProspectsPaginated) {
            combinedProspectsPagination = combinedProspectsResponseData as PaginatedProspectsAndCampaignProspectsResponse
            combinedProspects = combinedProspectsPagination.results || []

            // Update cached summary stats only when filter key changes (not on pagination)
            if (combinedProspectsPagination.summary_stats) {
                if (
                    cachedSummaryStatsRef.current.filterKey !== statsFilterKey
                ) {
                    cachedSummaryStatsRef.current = {
                        stats: combinedProspectsPagination.summary_stats,
                        filterKey: statsFilterKey,
                    }
                }
            }
        } else if (Array.isArray(combinedProspectsResponseData)) {
            combinedProspects = combinedProspectsResponseData as ProspectOrCampaignProspect[]
        }
    }

    // Extract and normalize Leads data
    const leadsResponseData = leadsData?.data
    let leads: Lead[] = []
    let leadsPagination: PaginatedLeadsResponse | null = null
    const isLeadsPaginated =
        leadsResponseData &&
        typeof leadsResponseData === 'object' &&
        !Array.isArray(leadsResponseData) &&
        'results' in leadsResponseData

    if (leadsResponseData) {
        if (isLeadsPaginated) {
            leadsPagination = leadsResponseData as PaginatedLeadsResponse
            leads = leadsPagination.results || []

            // Update cached leads summary stats only when filter key changes (not on pagination)
            if (leadsPagination.summary_stats) {
                if (
                    cachedLeadsSummaryStatsRef.current.filterKey !==
                    leadsStatsFilterKey
                ) {
                    cachedLeadsSummaryStatsRef.current = {
                        stats: leadsPagination.summary_stats,
                        filterKey: leadsStatsFilterKey,
                    }
                }
            }
        } else if (Array.isArray(leadsResponseData)) {
            leads = leadsResponseData as Lead[]
        }
    }

    // Calculate pagination info for combined prospects
    const pageSize = filters.page_size || 25
    const currentPage = filters.page || 1

    // Use cached summary stats if available, otherwise use current response stats
    const summaryStats =
        cachedSummaryStatsRef.current.stats ||
        (combinedProspectsPagination?.summary_stats ?? null)

    // Use cached leads summary stats if available, otherwise use current response stats
    const leadsSummaryStats =
        cachedLeadsSummaryStatsRef.current.stats ||
        (leadsPagination?.summary_stats ?? null)

    const combinedProspectsPaginationInfo = combinedProspectsPagination
        ? {
              count: combinedProspectsPagination.count,
              next: combinedProspectsPagination.next,
              previous: combinedProspectsPagination.previous,
              currentPage,
              pageSize,
              totalPages: Math.ceil(
                  combinedProspectsPagination.count / pageSize,
              ),
              summary_stats: summaryStats,
          }
        : {
              count: combinedProspects.length,
              next: null,
              previous: null,
              currentPage,
              pageSize,
              totalPages: Math.ceil(combinedProspects.length / pageSize),
              summary_stats: summaryStats,
          }

    const leadsPaginationInfo = leadsPagination
        ? {
              count: leadsPagination.count,
              next: leadsPagination.next,
              previous: leadsPagination.previous,
              currentPage,
              pageSize,
              totalPages: Math.ceil(leadsPagination.count / pageSize),
              summary_stats: leadsSummaryStats,
          }
        : {
              count: leads.length,
              next: null,
              previous: null,
              currentPage,
              pageSize,
              totalPages: Math.ceil(leads.length / pageSize),
              summary_stats: leadsSummaryStats,
          }

    // Mutation to add prospect to campaign
    const addProspectToCampaign = useMutation({
        mutationFn: ({
            prospectId,
            campaignId,
        }: {
            prospectId: string | number
            campaignId: string | number
        }) =>
            axiosInstance.post(
                `/outreach/prospects/${prospectId}/add-to-campaign/`,
                {
                    campaign_id: campaignId,
                },
            ),
        onSuccess: () => {
            // Invalidate the combined prospects query to refresh the data
            queryClient.invalidateQueries({
                queryKey: ['prospects-and-campaign-prospects'],
                exact: false,
            })
            toast.success('Prospect added to campaign successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to add prospect to campaign'
            toast.error(errorMessage)
        },
    })

    const removeProspectFromCampaign = useMutation({
        mutationFn: ({
            campaignProspectId,
        }: {
            campaignProspectId: string | number
        }) =>
            axiosInstance.post(
                `/outreach/prospects/${campaignProspectId}/remove-from-campaign/`,
            ),
        onSuccess: () => {
            // Invalidate the prospect list detail query to refresh the data
            queryClient.invalidateQueries({
                queryKey: ['prospects-and-campaign-prospects'],
                exact: false,
            })
            toast.success('Prospect removed from campaign successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to remove prospect from campaign'
            toast.error(errorMessage)
        },
    })

    const syncLeadMutation = useMutation({
        mutationFn: ({ leadId }: { leadId: number }) =>
            axiosInstance.post(`/outreach/leads/${leadId}/sync-to-entrata/`),
        onSuccess: (res: AxiosResponse<Lead>) => {
            const updatedLead: Lead = res.data

            // Update the leads query cache
            queryClient.setQueryData<
                AxiosResponse<PaginatedLeadsResponse | Lead[]>
            >(['leads', apartmentId, serializedLeadsFilters], (oldData) => {
                if (!oldData) return oldData

                const responseData = oldData.data

                // Handle paginated response
                if (
                    responseData &&
                    typeof responseData === 'object' &&
                    !Array.isArray(responseData) &&
                    'results' in responseData
                ) {
                    const paginatedData = responseData as PaginatedLeadsResponse
                    const updatedResults = paginatedData.results.map((lead) =>
                        lead.id === updatedLead.id ? updatedLead : lead,
                    )
                    return {
                        ...oldData,
                        data: {
                            ...paginatedData,
                            results: updatedResults,
                        },
                    }
                }
                // Handle array response
                else if (Array.isArray(responseData)) {
                    const updatedResults = responseData.map((lead) =>
                        lead.id === updatedLead.id ? updatedLead : lead,
                    )
                    return {
                        ...oldData,
                        data: updatedResults,
                    }
                }

                return oldData
            })

            toast.success('Lead synced successfully')
            return updatedLead
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to sync lead'
            toast.error(errorMessage)
        },
    })

    const syncLead = (leadId: number) => {
        syncLeadMutation.mutate({ leadId })
    }

    // Mutation to convert prospect/campaign prospect to lead
    const convertToLeadMutation = useMutation({
        mutationFn: (
            request: GuestCardFormRequest & {
                guest_card_id: number | string
                campaign_id?: number | string
                prospect_id?: number
                campaign_prospect_id?: number
                walk_in?: boolean
                leasing_season_id?: number
            },
        ) => axiosInstance.post('/outreach/leads/convert-to-lead/', request),
        onSuccess: () => {
            // Invalidate prospects query to remove the converted prospect from the list
            queryClient.invalidateQueries({
                queryKey: ['prospects-and-campaign-prospects'],
                exact: false,
            })
            // Also invalidate leads query in case we want to show the new lead
            queryClient.invalidateQueries({
                queryKey: ['leads'],
                exact: false,
            })
            invalidatePipelineCounts(queryClient)
            toast.success('Prospect converted to lead successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to convert prospect to lead'
            toast.error(errorMessage)
        },
    })

    const convertToLead = (
        request: GuestCardFormRequest & {
            guest_card_id: number | string
            campaign_id?: number | string
            prospect_id?: number
            campaign_prospect_id?: number
            walk_in?: boolean
            leasing_season_id?: number
        },
    ) => {
        convertToLeadMutation.mutate(request)
    }

    const approveApplicationMutation = useMutation({
        mutationFn: async (leadId: number) => {
            const { data } = await axiosInstance.post<Lead>(
                `/outreach/leads/${leadId}/approve-application/`,
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['leads'], exact: false })
            invalidatePipelineCounts(queryClient)
            toast.success('Application approved — moved to Screening')
        },
        onError: (error: { response?: { data?: { error?: string } } }) => {
            toast.error(
                error?.response?.data?.error ?? 'Failed to approve application',
            )
        },
    })

    const resendApplicationEmailMutation = useMutation({
        mutationFn: async (leadId: number) => {
            const { data } = await axiosInstance.post<{
                application_apply_url: string
                application_status: string | null
                sent_to: string | null
            }>(`/outreach/leads/${leadId}/resend-application-email/`)
            return data
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['leads'], exact: false })
            invalidatePipelineCounts(queryClient)
            const sentTo = data.sent_to ? ` to ${data.sent_to}` : ''
            toast.success(`Application reminder sent${sentTo}`)
        },
        onError: (error: { response?: { data?: { error?: string } } }) => {
            toast.error(
                error?.response?.data?.error ??
                    'Failed to send application reminder',
            )
        },
    })

    const sendScreeningLinkMutation = useMutation({
        mutationFn: async ({
            leadId,
            packageSlug,
        }: {
            leadId: number
            packageSlug?: string
        }) => {
            const { data } = await axiosInstance.post<{
                lead: Lead
                invitation_url: string
                candidate_id: string
                invitation_id: string
                sent_to: string | null
            }>(`/outreach/leads/${leadId}/send-screening-link/`, {
                ...(packageSlug ? { package: packageSlug } : {}),
            })
            return data
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['leads'], exact: false })
            invalidatePipelineCounts(queryClient)
            const sentTo = data.sent_to ? ` to ${data.sent_to}` : ''
            toast.success(`Screening link sent${sentTo}`)
        },
        onError: (error: { response?: { data?: { error?: string } } }) => {
            toast.error(
                error?.response?.data?.error ?? 'Failed to send screening link',
            )
        },
    })

    const sendLeasePackageMutation = useMutation({
        mutationFn: async ({
            leadId,
            guarantorEmail,
        }: {
            leadId: number
            guarantorEmail?: string
        }) => {
            const { data } = await axiosInstance.post<{
                lead: Lead
                documents: Array<{
                    signable_doc_id: number
                    signable_doc_name: string
                    document_type: string
                    apply_url: string
                }>
                sent_to_applicant: string | null
                sent_to_guarantor: string | null
                skipped_already_sent: string[]
            }>(`/outreach/leads/${leadId}/send-lease-package/`, {
                ...(guarantorEmail ? { guarantor_email: guarantorEmail } : {}),
            })
            return data
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['leads'], exact: false })
            invalidatePipelineCounts(queryClient)
            const count = data.documents?.length ?? 0
            const parts = [
                `Lease package sent (${count} doc${count === 1 ? '' : 's'})`,
            ]
            if (data.sent_to_applicant) {
                parts.push(`applicant: ${data.sent_to_applicant}`)
            }
            if (data.sent_to_guarantor) {
                parts.push(`guarantor: ${data.sent_to_guarantor}`)
            }
            toast.success(parts.join(' · '))
        },
        onError: (error: { response?: { data?: { error?: string } } }) => {
            toast.error(
                error?.response?.data?.error ?? 'Failed to send lease package',
            )
        },
    })

    const refreshLeasePackageMutation = useMutation({
        mutationFn: async (leadId: number) => {
            const { data } = await axiosInstance.post<{
                lead: Lead
                lease_package: Lead['lease_package']
            }>(`/outreach/leads/${leadId}/refresh-lease-package/`)
            return data
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['leads'], exact: false })
            invalidatePipelineCounts(queryClient)
            const status = data.lease_package?.status
            if (status === 'awaiting_property') {
                toast.success(
                    'Lease package ready for property manager signature',
                )
            } else if (status === 'signed') {
                toast.success('Lease package fully signed')
            } else {
                toast.success('Lease package status refreshed')
            }
        },
        onError: (error: { response?: { data?: { error?: string } } }) => {
            toast.error(
                error?.response?.data?.error ??
                    'Failed to refresh lease package status',
            )
        },
    })

    const signLeasePackageAsPropertyMutation = useMutation({
        mutationFn: async ({
            leadId,
            notify,
        }: {
            leadId: number
            notify?: boolean
        }) => {
            const { data } = await axiosInstance.post<{
                lead: Lead
                lease_package: Lead['lease_package']
                property_signing_urls: Array<{
                    signable_doc_id: string
                    signable_doc_name: string
                    signing_url: string
                }>
                notified: boolean
            }>(`/outreach/leads/${leadId}/lease-package-property-sign/`, {
                ...(notify ? { notify: true } : {}),
            })
            return data
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['leads'], exact: false })
            invalidatePipelineCounts(queryClient)
            const urls = data.property_signing_urls ?? []
            if (urls.length === 0) {
                toast.error('No property manager signing link available yet')
                return
            }
            urls.forEach((item) => {
                if (item.signing_url) {
                    window.open(
                        item.signing_url,
                        '_blank',
                        'noopener,noreferrer',
                    )
                }
            })
            toast.success(
                urls.length === 1
                    ? 'Opened property manager signing link'
                    : `Opened ${urls.length} property manager signing links`,
            )
        },
        onError: (error: { response?: { data?: { error?: string } } }) => {
            toast.error(
                error?.response?.data?.error ??
                    'Failed to open property manager signing',
            )
        },
    })

    const startScreeningMutation = useMutation({
        mutationFn: async ({
            leadId,
            packageSlug,
        }: {
            leadId: number
            packageSlug?: string
        }) => {
            const { data } = await axiosInstance.post<{
                lead: Lead
                invitation_url: string
                candidate_id: string
                invitation_id: string
            }>(`/outreach/leads/${leadId}/start-screening/`, {
                ...(packageSlug ? { package: packageSlug } : {}),
            })
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['leads'], exact: false })
            invalidatePipelineCounts(queryClient)
            toast.success('Screening invitation created')
        },
        onError: (error: { response?: { data?: { error?: string } } }) => {
            toast.error(
                error?.response?.data?.error ?? 'Failed to start screening',
            )
        },
    })

    const resendScreeningInvitationMutation = useMutation({
        mutationFn: async (leadId: number) => {
            const { data } = await axiosInstance.post<{
                lead: Lead
                invitation_url: string
                candidate_id: string
                invitation_id: string
            }>(`/outreach/leads/${leadId}/resend-screening-invitation/`)
            return data
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['leads'], exact: false })
            invalidatePipelineCounts(queryClient)
            if (data.invitation_url) {
                toast.success('Screening invite ready')
            } else {
                toast.success('Screening invitation updated')
            }
        },
        onError: (error: { response?: { data?: { error?: string } } }) => {
            toast.error(
                error?.response?.data?.error ??
                    'Failed to resend screening invitation',
            )
        },
    })

    return {
        // Combined Prospects and Campaign Prospects data
        combinedProspects,
        combinedProspectsPagination: combinedProspectsPaginationInfo,
        isLoadingCombinedProspects,
        errorCombinedProspects,
        refetchCombinedProspects,

        // Leads data
        leads,
        leadsPagination: leadsPaginationInfo,
        isLoadingLeads,
        errorLeads,
        refetchLeads,
        syncLead,

        // Combined loading state
        isLoading: isLoadingCombinedProspects || isLoadingLeads,

        // Mutations
        addProspectToCampaign,
        convertToLead,
        convertToLeadMutation,
        approveApplication: approveApplicationMutation.mutateAsync,
        isApprovingApplication: approveApplicationMutation.isLoading,
        resendApplicationEmail: resendApplicationEmailMutation.mutateAsync,
        isResendingApplicationEmail: resendApplicationEmailMutation.isLoading,
        sendScreeningLink: (leadId: number, packageSlug?: string) =>
            sendScreeningLinkMutation.mutateAsync({ leadId, packageSlug }),
        isSendingScreeningLink: sendScreeningLinkMutation.isLoading,
        sendLeasePackage: (leadId: number, guarantorEmail?: string) =>
            sendLeasePackageMutation.mutateAsync({ leadId, guarantorEmail }),
        isSendingLeasePackage: sendLeasePackageMutation.isLoading,
        refreshLeasePackage: (leadId: number) =>
            refreshLeasePackageMutation.mutateAsync(leadId),
        isRefreshingLeasePackage: refreshLeasePackageMutation.isLoading,
        signLeasePackageAsProperty: (leadId: number, notify?: boolean) =>
            signLeasePackageAsPropertyMutation.mutateAsync({ leadId, notify }),
        isSigningLeasePackageAsProperty:
            signLeasePackageAsPropertyMutation.isLoading,
        startScreening: (leadId: number, packageSlug?: string) =>
            startScreeningMutation.mutateAsync({ leadId, packageSlug }),
        isStartingScreening: startScreeningMutation.isLoading,
        resendScreeningInvitation:
            resendScreeningInvitationMutation.mutateAsync,
        isResendingScreeningInvitation:
            resendScreeningInvitationMutation.isLoading,

        // Type guard helper (exported for use in components)
        isDetailedCampaignProspect,
    }
}
